/**
 * Analytic HaxBall segment collision routines from the existing BFF predictor.
 *
 * HaxBall represents rounded walls as circular arcs (a segment with a
 * `curve` angle).  Treating those walls as a sampled polyline causes visible
 * jitter and lets a fast ball tunnel through corners.  These helpers mirror
 * the analytic construction used by that predictor: straight
 * segments use a signed normal and curved segments use their exact circle,
 * tangents and bias handling.
 */

const EPSILON = 1e-12;

const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

function point(value, fallback = { x: 0, y: 0 }) {
  if (Array.isArray(value)) return { x: finite(value[0]), y: finite(value[1]) };
  if (!value || typeof value !== 'object') return { ...fallback };
  if (value.position) return point(value.position, fallback);
  return { x: finite(value.x ?? value[0]), y: finite(value.y ?? value[1]) };
}

function normalise(x, y) {
  const length = Math.hypot(x, y);
  return length > EPSILON ? [x / length, y / length] : [1, 0];
}

/**
 * Resolve a segment's vertex references and precompute native arc geometry.
 * The input is never mutated, so a stadium can safely be reused for previews.
 */
export function prepareNativeSegment(raw = {}, vertexes = []) {
  const source = { ...raw };
  const v0 = point(source.a || source.p0 || (Number.isInteger(source.v0) ? vertexes[source.v0] : source.v0));
  const v1 = point(source.b || source.p1 || (Number.isInteger(source.v1) ? vertexes[source.v1] : source.v1));
  const segment = {
    ...source,
    v0: [v0.x, v0.y],
    v1: [v1.x, v1.y],
    bCoef: finite(source.bCoef ?? source.bounce ?? source.m, 1),
    bias: finite(source.bias, 0),
    nativePrepared: true
  };

  let curve = source.curveF !== undefined ? null : finite(source.curve ?? source.curveAngle, 0);
  if (curve !== null) {
    curve *= Math.PI / 180;
    // Native normalises negative arcs by reversing their endpoints and bias.
    if (curve < 0) {
      curve = -curve;
      segment.bias = -segment.bias;
      const swap = segment.v0;
      segment.v0 = segment.v1;
      segment.v1 = swap;
    }
    if (curve > 0.17435839227423353 && curve < 5.934119456780721) {
      segment.curveF = 1 / Math.tan(curve / 2);
    }
    segment.curveAngle = curve;
  }

  const a = { x: segment.v0[0], y: segment.v0[1] };
  const b = { x: segment.v1[0], y: segment.v1[1] };
  if (segment.curveF !== undefined) {
    const halfX = 0.5 * (b.x - a.x);
    const halfY = 0.5 * (b.y - a.y);
    const center = {
      x: a.x + halfX - halfY * segment.curveF,
      y: a.y + halfY + halfX * segment.curveF
    };
    const radius = Math.hypot(a.x - center.x, a.y - center.y);
    segment.circleCenter = [center.x, center.y];
    segment.circleRadius = radius;
    segment.v0Tan = [-(a.y - center.y), a.x - center.x];
    segment.v1Tan = [-(center.y - b.y), center.x - b.x];
    if (segment.curveF <= 0) {
      segment.v0Tan[0] *= -1;
      segment.v0Tan[1] *= -1;
      segment.v1Tan[0] *= -1;
      segment.v1Tan[1] *= -1;
    }
  } else {
    const dx = a.x - b.x;
    const dy = -(a.y - b.y);
    const length = Math.hypot(dx, dy);
    segment.normal = [dy / (length || 1), dx / (length || 1)];
  }
  return segment;
}

/**
 * Resolve one disc against a prepared/native segment.
 * Returns true when the disc was corrected. The disc uses the plain state
 * shape from haxballPhysics (`position`, `velocity`, `radius`, `bCoef`).
 */
export function resolveNativeSegmentCollision(disc, rawSegment, vertexes = []) {
  if (!disc || !rawSegment) return false;
  const segment = rawSegment.nativePrepared === true
    ? rawSegment
    : prepareNativeSegment(rawSegment, vertexes);
  const position = point(disc.position);
  let distance;
  let coefX;
  let coefY;

  if (segment.curveF === undefined) {
    const a = { x: segment.v0[0], y: segment.v0[1] };
    const b = { x: segment.v1[0], y: segment.v1[1] };
    const segX = b.x - a.x;
    const segY = b.y - a.y;
    const d0X = position.x - a.x;
    const d0Y = position.y - a.y;
    const d1X = position.x - b.x;
    const d1Y = position.y - b.y;
    if (d0X * segX + d0Y * segY <= 0 || d1X * segX + d1Y * segY >= 0) return false;
    const norm = normalise(a.y - b.y, b.x - a.x);
    coefX = -norm[0];
    coefY = -norm[1];
    distance = coefX * d1X + coefY * d1Y;
  } else {
    const center = segment.circleCenter;
    const dx = position.x - center[0];
    const dy = position.y - center[1];
    const tan1 = segment.v1Tan;
    const tan0 = segment.v0Tan;
    if ((tan1[0] * dx + tan1[1] * dy > 0 && tan0[0] * dx + tan0[1] * dy > 0) === (segment.curveF <= 0)) return false;
    const length = Math.hypot(dx, dy);
    if (length <= EPSILON) return false;
    distance = length - segment.circleRadius;
    coefX = dx / length;
    coefY = dy / length;
  }

  let bias = segment.bias;
  if (bias === 0 || bias === undefined) {
    if (distance < 0) {
      distance = -distance;
      coefX = -coefX;
      coefY = -coefY;
    }
  } else if (bias < 0) {
    bias = -bias;
    distance = -distance;
    coefX = -coefX;
    coefY = -coefY;
    if (distance < -bias) return false;
  }
  if (!(distance < finite(disc.radius, 0))) return false;

  const correction = finite(disc.radius, 0) - distance;
  disc.position = {
    x: position.x + coefX * correction,
    y: position.y + coefY * correction
  };
  const velocity = point(disc.velocity);
  const speedCoef = coefX * velocity.x + coefY * velocity.y;
  if (speedCoef < 0) {
    const impulse = speedCoef * (finite(disc.bCoef, 0) * finite(segment.bCoef, 1) + 1);
    disc.velocity = {
      x: velocity.x - coefX * impulse,
      y: velocity.y - coefY * impulse
    };
  }
  return true;
}

export default prepareNativeSegment;
